import React from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown,
  Label,
  Input,
  FlexGrid,
  Checkbox,
  TextArea,
} from '@gasbuddy/react-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

import Button from '../../../components/button';
import ChipList from '../../../components/chip-list';
import EmailChipList from '../../../components/email-chip-list';
import Notification from '../../../components/notification';
import ComparisonMetricSelector from '../../../components/comparison-metric-selector';
import StationSelector from '../../../components/station-selector';
import styles from './styles.css';
import { JOBS, SENTIMENT, ORIGINAL_STATUSES, CHANGED_STATUSES, convertStatusToDisplayName } from '../utils';
import { RatingCategoryListPropTypes, StationListsPropTypes, JobPropTypes, BasicStationsPropTypes, BrandPropTypes } from '../prop-types';
import { Solutions } from '../../../../lib/constants/enums';
import StationListSelector from '../../../components/station-list-selector-v2';
import BPModal from '../../../components/modal';
import CompetitionSelector from '../../../components/competition-selector';

const { api } = require('../../../../lib/bputils').default;

const HOURLY_JOB_SCHEDULE = 'every-hour';

const MONDAY_MORNING_SCEHDULE = 'every-monday-morning-5:30-am';

const BUSINESS_PAGES_APP_NAME = 'business_pages';

const SENTIMENTS = [SENTIMENT.POSITIVE, SENTIMENT.NEUTRAL, SENTIMENT.NEGATIVE];

const PDF_JOBS = [
  JOBS.STATION_PROFILE_PDF,
  JOBS.STATION_REVIEWS_PDF,
  JOBS.STATION_RATINGS_PDF,
  JOBS.BRAND_ENGAGEMENT_PDF,
  JOBS.COMPETITOR_BENCHMARK_PDF,
];

export default function JobModal({
  showModal,
  onClose,
  onAdd,
  onDelete,
  onEdit,
  stations,
  stationLists,
  ratingCategories,
  job,
  brands,
  organizationSolutions,
  email,
}) {
  const [loading, setLoading] = React.useState(true);

  const [jobName, setJobName] = React.useState(job?.display_name || '');
  const [selectedJobType, setSelectedJobType] = React.useState(job?.job_type?.name || '');
  const [enableJob, setEnableJob] = React.useState(job?.enabled || true);

  /** Station Targets */
  const [stationList, setStationList] = React.useState(
    job?.parameters?.station_list_id ? stationLists.find(sl => sl.id === job.parameters.station_list_id) : null,
  );
  const [station, setStation] = React.useState(null);
  const [stationSelectorLoaded, setStationSelectorLoaded] = React.useState(false);

  const [jobTypeError, setJobTypeError] = React.useState('');
  const [jobNameError, setJobNameError] = React.useState('');
  const [recipientsError, setRecipientsError] = React.useState('');

  /** Review Keyword specific settings */
  const [reviewSentiments, setReviewSentiments] = React.useState(job?.parameters?.sentiment_filter || []);
  const [keywords, setKeywords] = React.useState(job?.parameters?.keywords || []);

  /** Rating Category Score Change specific settings */
  const [selectedCategories, setSelectedCategories] = React.useState(job?.parameters?.category_ids || []);
  const [originalStatuses, setOriginalStatuses] = React.useState(job?.parameters?.change_from || []);
  const [changedStatuses, setChangedStatuses] = React.useState(job?.parameters?.change_to || []);

  /** PDF specific settings */
  const [comparison, setComparison] = React.useState(null);
  const [recipients, setRecipients] = React.useState(job?.parameters?.recipients || []);
  const [comments, setComments] = React.useState(job?.parameters?.comments || '');
  const [ratingCategory, setRatingCategory] = React.useState(job?.parameters?.category_id || null);

  /** Competitor Benchmarking specific settings */
  const [brandOne, setBrandOne] = React.useState(job?.parameters?.brand_1 || '');
  const [brandTwo, setBrandTwo] = React.useState(job?.parameters?.brand_2 || '');
  const [brandThree, setBrandThree] = React.useState(job?.parameters?.brand_3 || '');
  const [competitorComparison, setCompetitorComparison] = React.useState(job?.parameter?.filter || null);

  const [brandTwoError, setBrandTwoError] = React.useState('');
  const [brandThreeError, setBrandThreeError] = React.useState('');


  /** dropdown options */
  const [brandOptions, setBrandOptions] = React.useState([]);
  const [jobTypeOptions, setJobTypeOptions] = React.useState([]);
  const [ratingCategoryOptions, setRatingCategoryOptions] = React.useState([]);

  const isSuperAdminUser = (email || '').endsWith('@gasbuddy.io');

  React.useEffect(() => {
    if (Array.isArray(organizationSolutions)) {
      getJobTypes();
    }
  }, [organizationSolutions]);

  React.useEffect(() => {
    if (jobTypeError.length && selectedJobType) {
      setJobTypeError('');
    }
  }, [selectedJobType]);

  React.useEffect(() => {
    if (brands?.length) {
      setBrandOptions(brands.map(b => ({ label: b.display_name, value: b.id.toString() })));
    }
  }, [brands]);

  React.useEffect(() => {
    if (ratingCategories?.length) {
      setRatingCategoryOptions(ratingCategories.map(rc => ({ value: rc.id.toString(), label: rc.name })));
    }
  }, [ratingCategories]);

  React.useEffect(() => {
    if (job) {
      const jobType = job.job_type.name;
      setJobName(job.display_name);
      setSelectedJobType(jobType);
      setEnableJob(job.enabled);

      if (job.parameters.station_list_id) {
        setStationList(stationLists.find(sl => sl.id === job.parameters.station_list_id));
      }
      if (job.parameters.station_id) {
        setStation(stations.find(s => s.id === job.parameters.station_id));
      }
      setReviewSentiments(job?.parameters?.sentiment_filter || []);
      setKeywords(job?.parameters?.keywords || []);
      setSelectedCategories(job?.parameters?.category_ids || []);
      setOriginalStatuses(job?.parameters?.change_from || []);
      setChangedStatuses(job?.parameters?.change_to || []);
      setRecipients(job?.parameters?.recipients || []);
      setComments(job?.parameters?.comments || '');
      setComparison(job?.parameters?.comparison || null);
      setBrandOne(job?.parameters?.brand_1 || '');
      setBrandTwo(job?.parameters?.brand_2 || '');
      setBrandThree(job?.parameters?.brand_3 || '');
    }
  }, [job]);

  React.useEffect(() => {
    if (stationLists?.length) {
      setStationList(stationLists[0]);
    }
  }, [stationLists]);

  React.useEffect(() => {
    if (jobTypeOptions) {
      if (selectedJobType === JOBS.STATION_PROFILE_PDF && stationSelectorLoaded) {
        setLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, [jobTypeOptions, stationSelectorLoaded, selectedJobType]);

  const getJobTypeOptions = jobTypes => jobTypes.map((jt) => {
    if (jt.name === JOBS.STATION_PROFILE_PDF) {
      if (!isSuperAdminUser && !organizationSolutions.includes(Solutions.Listings)) {
        return null;
      }
      if (jt.name === JOBS.STATION_REVIEWS_PDF || jt.name === JOBS.STATION_RATINGS_PDF) {
        if (!isSuperAdminUser && !organizationSolutions.includes(Solutions.ReputationManagement)) {
          return null;
        }
      }
      if (jt.name === JOBS.BRAND_ENGAGEMENT_PDF || jt.name === JOBS.COMPETITOR_BENCHMARK_PDF) {
        if (!isSuperAdminUser && !organizationSolutions.includes(Solutions.Insights)) {
          return null;
        }
      }
    }
    return {
      value: jt.name,
      label: jt.display_name,
    };
  }).filter(jt => !!jt);


  const getJobTypes = async () => api.get({
    requestId: 'getAllJobTypes',
    path: '/jobs/types',
    success: (res) => {
      setJobTypeOptions(getJobTypeOptions((res?.job_types || [])));
    },
    error: () => {
      Notification.error('Unable to fetch job types');
    },
  });

  const handleOnSaveClick = () => {
    clearValidationErrors();
    let errorFound = false;
    if (!selectedJobType) {
      setJobTypeError('Please select a job type to continue');
    }
    if (!jobName.length) {
      setJobNameError('Please enter a name for your job.');
      errorFound = true;
    }
    if (PDF_JOBS.includes(selectedJobType)) {
      if (!recipients.length) {
        setRecipientsError('Please enter at least one recipient');
        errorFound = true;
      }
    }
    if (selectedJobType === JOBS.COMPETITOR_BENCHMARK_PDF) {
      if (brandOne === brandTwo) {
        setBrandTwoError('Please select a different brand');
        errorFound = true;
      }
      if (brandOne === brandThree || brandTwo === brandThree) {
        setBrandThreeError('Please select a different brand');
        errorFound = true;
      }
    }
    if (!errorFound) {
      saveJob();
      return true;
    }
    return false;
  };

  const getJobSchedule = () => {
    if (
      selectedJobType === JOBS.REVIEW_KEYWORD
      || selectedJobType === JOBS.RATING_CATEGORY_SCORE_CHANGE
    ) {
      return HOURLY_JOB_SCHEDULE;
    }
    if (PDF_JOBS.includes(selectedJobType)) {
      return MONDAY_MORNING_SCEHDULE;
    }
    return HOURLY_JOB_SCHEDULE;
  };

  const clearValidationErrors = () => {
    if (jobTypeError.length && selectedJobType) {
      setJobTypeError('');
    }
    if (jobNameError.length && jobName.length) {
      setJobNameError('');
    }
    if (recipientsError.length && recipients.length) {
      setRecipientsError('');
    }
    if (brandOne !== brandTwo) {
      setBrandTwoError('');
    }
    if (brandOne !== brandThree && brandTwo !== brandThree) {
      setBrandThreeError('');
    }
  };

  const getJobPayload = () => {
    const jobPayload = {
      display_name: jobName,
      job_type: selectedJobType,
      job_schedule: getJobSchedule(),
      enabled: enableJob,
      job_options: {
      },
      app_name: BUSINESS_PAGES_APP_NAME,
    };

    if (selectedJobType === JOBS.REVIEW_KEYWORD) {
      jobPayload.job_options.keywords = keywords;
      jobPayload.job_options.sentiment_filter = reviewSentiments;
    }

    if (selectedJobType === JOBS.RATING_CATEGORY_SCORE_CHANGE) {
      jobPayload.job_options.category_ids = selectedCategories;
      jobPayload.job_options.change_from = originalStatuses;
      jobPayload.job_options.change_to = changedStatuses;
    }

    if (PDF_JOBS.includes(selectedJobType)) {
      if (selectedJobType !== JOBS.STATION_REVIEWS_PDF && selectedJobType !== JOBS.COMPETITOR_BENCHMARK_PDF) {
        jobPayload.job_options.comparison = {
          type: comparison.metricApiName,
          target: comparison.target,
        };
      }

      jobPayload.job_options.recipients = recipients;
      jobPayload.job_options.comments = comments;
    }

    if (selectedJobType !== JOBS.STATION_PROFILE_PDF) {
      jobPayload.job_options.station_list_id = stationList?.id;
    } else {
      jobPayload.job_options.station_id = station.id;
    }

    if (selectedJobType === JOBS.COMPETITOR_BENCHMARK_PDF) {
      jobPayload.job_options.brand_1 = brandOne;
      jobPayload.job_options.brand_2 = brandTwo;
      jobPayload.job_options.brand_3 = brandThree;
      jobPayload.job_options.filter = competitorComparison;
    }

    if (selectedJobType === JOBS.STATION_RATINGS_PDF) {
      jobPayload.job_options.category_id = ratingCategory;
    }

    return jobPayload;
  };

  const saveJob = () => {
    if (job?.id) {
      updateJob();
    } else if (jobName.length) {
      const jobPayload = getJobPayload();
      api.post({
        path: '/jobs',
        data: jobPayload,
        success: (res) => {
          onAdd(res);
          handleOnClose();
          Notification.success('The report was successfully added');
        },
        error: () => {
          Notification.error('Unable to save the report');
        },
      });
    }
  };

  const updateJob = () => {
    const jobPayload = getJobPayload();
    api.put({
      path: `/jobs/${job.id}`,
      data: jobPayload,
      requestId: `putJob${job.id}`,
      success: () => {
        if (onEdit) {
          onEdit(job.id, jobPayload);
        }
        handleOnClose();
        Notification.success('Your report settings have been updated');
      },
      error: () => {
        Notification.error('Unable to update the report');
      },
    });
  };

  const handleOnStationListChange = (stationListId) => {
    setStationList(stationLists.find(sl => sl.id === stationListId));
  };

  const resetGeneralJobSettings = () => {
    if (selectedJobType.length) {
      setSelectedJobType('');
    }
    if (jobTypeError.length) {
      setJobTypeError('');
    }
    if (jobName.length) {
      setJobName('');
    }
    if (!enableJob) {
      setEnableJob(true);
    }
    if (jobNameError.length) {
      setJobNameError('');
    }
    if (recipientsError.length) {
      setRecipientsError('');
    }
    setStationList(stationLists[0]);
  };

  const resetReviewKeywordSettings = () => {
    if (reviewSentiments.length) {
      setReviewSentiments([]);
    }
    if (keywords.length) {
      setKeywords([]);
    }
  };

  const resetRatingCategoryScoreChangeSettings = () => {
    if (selectedCategories.length) {
      setSelectedCategories([]);
    }
    if (originalStatuses.length) {
      setOriginalStatuses([]);
    }
    if (changedStatuses.length) {
      setChangedStatuses([]);
    }
  };

  const resetPDFSettings = () => {
    if (recipients.length) {
      setRecipients([]);
    }
    if (comments.length) {
      setComments('');
    }
    if (station) {
      setStation(null);
    }
    if (comparison) {
      setComparison(null);
    }
  };

  const resetStationRatingsSettings = () => {
    resetPDFSettings();
    if (ratingCategory) {
      setRatingCategory('');
    }
  };

  const resetCompetitorBenchmarkSettings = () => {
    resetPDFSettings();
    if (brandOne) {
      setBrandOne('');
    }
    if (brandTwo) {
      setBrandTwo('');
    }
    if (brandThree) {
      setBrandThree('');
    }
  };


  const resetModal = () => {
    resetGeneralJobSettings();
    switch (selectedJobType) {
      case JOBS.REVIEW_KEYWORD:
        resetReviewKeywordSettings();
        break;
      case JOBS.RATING_CATEGORY_SCORE_CHANGE:
        resetRatingCategoryScoreChangeSettings();
        break;
      case JOBS.STATION_PROFILE_PDF:
      case JOBS.STATION_REVIEWS_PDF:
        resetPDFSettings();
        break;
      case JOBS.STATION_RATINGS_PDF:
        resetStationRatingsSettings();
        break;
      case JOBS.COMPETITOR_BENCHMARK_PDF:
        resetCompetitorBenchmarkSettings();
        break;
      default:
        break;
    }
  };


  const handleOnClose = () => {
    onClose();
    resetModal();
  };

  const handleOnDelete = () => {
    api.delete({
      path: `/jobs/${job.id}`,
      success: () => {
        if (onDelete) {
          onDelete(job.id);
        }
        handleOnClose();
        Notification.success('The report was successfully deleted');
      },
      error: () => {
        Notification.error('Unable to delete the report');
      },
    });
  };

  const handleOnCheckboxChange = (values, value, setter, add) => {
    if (add) {
      setter([...values, value]);
    } else {
      setter(values.filter(s => s !== value));
    }
  };

  const isCheckboxChecked = (values, value) => values.includes(value);

  const isArrayEqual = (arr1, arr2) => arr1.slice().sort().join(',') === arr2.slice().sort().join(',');

  const handleOnAllCheckbox = (values, allValues, setter) => {
    if (isArrayEqual(allValues, values)) {
      setter([]);
    } else {
      setter(allValues);
    }
  };

  const renderReviewKeywordSettings = () => (
    <>
      <FlexGrid.Column className={styles.bottomMargin}>
        <Label>
          <span className={styles.labelText}>
            Keywords
          </span>
          <ChipList placeholder="Add keywords" onChange={setKeywords} values={keywords} />
        </Label>
      </FlexGrid.Column>
      <FlexGrid.Column className={styles.bottomMargin}>
        <span>
          <b>Has sentiment:</b>
        </span>
        <FlexGrid>
          {SENTIMENTS.map(sentiment => (
            <FlexGrid.Column mobile={4} className={styles.checkboxContainer}>
              <Label className={styles.flexLabel}>
                <Checkbox
                  className={styles.alertCheckbox}
                  checked={isCheckboxChecked(
                    reviewSentiments,
                    sentiment.toLowerCase(),
                  )}
                  onChange={() => handleOnCheckboxChange(
                    reviewSentiments,
                    sentiment.toLowerCase(),
                    setReviewSentiments,
                    !isCheckboxChecked(
                      reviewSentiments,
                      sentiment.toLowerCase(),
                    ),
                  )}
                />
                <span>{sentiment}</span>
              </Label>
            </FlexGrid.Column>
          ))}
        </FlexGrid>
      </FlexGrid.Column>
    </>
  );

  const renderMultipleCheckbox = (allValues, setter, selectedValues, displayFunc) => (
    <FlexGrid.Column className={styles.bottomMargin}>
      <FlexGrid className={`${styles.inset} ${styles.bottomMargin}`}>
        <FlexGrid.Column mobile={6}>
          <b>Any of these Categories</b>
        </FlexGrid.Column>
        <FlexGrid.Column mobile={6}>
          <Label className={styles.flexLabel}>
            <Checkbox
              className={styles.alertCheckbox}
              checked={isArrayEqual(
                allValues,
                selectedValues,
              )}
              onChange={() => handleOnAllCheckbox(
                selectedValues,
                allValues,
                setter,
              )
              }
            />
            <span className={styles.labelText}>
              Select all
            </span>
          </Label>
        </FlexGrid.Column>
        <FlexGrid.Column>
          <FlexGrid className={styles.inset}>
            {allValues.map(value => (
              <FlexGrid.Column mobile={3}>
                <Label className={styles.flexLabel}>
                  <Checkbox
                    className={styles.alertCheckbox}
                    checked={isCheckboxChecked(selectedValues, value)}
                    onChange={() => handleOnCheckboxChange(
                      selectedValues,
                      value,
                      setter,
                      !isCheckboxChecked(selectedValues, value),
                    )
                    }
                  />
                  <span>{displayFunc(value)}</span>
                </Label>
              </FlexGrid.Column>
            ))}
          </FlexGrid>
        </FlexGrid.Column>
      </FlexGrid>
    </FlexGrid.Column>
  );

  const renderRatingScoreChangeSettings = () => (
    <>
      <FlexGrid.Column>
        <b style={{ fontSize: '18px' }}>
          Notify me when, in the Station Ratings...
        </b>
      </FlexGrid.Column>
      {
        renderMultipleCheckbox(
          ratingCategories.map(rc => rc.id.toString()),
          setSelectedCategories,
          selectedCategories,
          value => ratingCategories.find(rc => rc.id.toString() === value).name,
        )
      }
      {
        renderMultipleCheckbox(
          ORIGINAL_STATUSES,
          setOriginalStatuses,
          originalStatuses,
          value => convertStatusToDisplayName(value),
        )
      }
      {
        renderMultipleCheckbox(
          CHANGED_STATUSES,
          setChangedStatuses,
          changedStatuses,
          value => convertStatusToDisplayName(value),
        )
      }
    </>
  );

  const handleOnChipListChange = (e) => {
    setRecipients(e);
    if (recipientsError.length) {
      setRecipientsError('');
    }
  };

  const renderBasePDFParameters = () => (
    <>
      {(selectedJobType !== JOBS.STATION_REVIEWS_PDF && selectedJobType !== JOBS.COMPETITOR_BENCHMARK_PDF) ? renderComparisonMetricSelector() : null}
      {renderRecipientsChipList()}
      {renderCommentsText()}
    </>
  );

  const renderDropdown = (label, selectedValue, options, labedId, setter, dropdownError) => (
    <FlexGrid.Column>
      <Label htmlFor={labedId}>
        <span className={styles.labelText}>{label}</span>
        <Dropdown
          id={labedId}
          animated
          bordered
          value={selectedValue ? selectedValue.toString() : null}
          options={options}
          onChange={e => setter(e.target.value)}
        />
        {dropdownError && <span className={styles.errorMessage}>{dropdownError}</span>}
      </Label>
    </FlexGrid.Column>
  );

  const renderStationRatingsPDFParameters = () => (
    <>
      {renderDropdown('Rating Category', ratingCategory, ratingCategoryOptions, 'ratingCategoryDropdown', setRatingCategory)}
      {renderBasePDFParameters()}
    </>
  );

  const renderCompetitorBenchmarkingComparison = () => (
    <CompetitionSelector value={competitorComparison} onChange={setCompetitorComparison} />
  );

  const renderCompetitorBenchmarkPDFParameters = () => (
    <>
      {renderDropdown('Brand 1', brandOne, brandOptions, 'brandOneDropdown', setBrandOne)}
      {renderDropdown('Brand 2', brandTwo, brandOptions, 'brandTwoDropdown', setBrandTwo, brandTwoError)}
      {renderDropdown('Brand 3', brandThree, brandOptions, 'brandThreeDropdown', setBrandThree, brandThreeError)}
      {renderCompetitorBenchmarkingComparison()}
      {renderBasePDFParameters()}
    </>
  );

  const renderComparisonMetricSelector = () => (
    <FlexGrid.Column className={styles.rightPad}>
      <Label>
        <span className={styles.labelText}>
            Compare To
          <ComparisonMetricSelector
            showBrandMetric
            doNotDispatchChanges
            onChange={setComparison}
            gTagTitle="Report Management PDF Scheduler"
            doNotStoreChanges
            currentComparisonMetric={comparison ? {
              target: comparison.target || null,
              metricApiName: comparison.type || comparison.metricApiName,
            } : null}
          />
        </span>
      </Label>
    </FlexGrid.Column>
  );

  const renderRecipientsChipList = () => (
    <FlexGrid.Column>
      <EmailChipList recipients={recipients} onListChange={handleOnChipListChange} error={recipientsError} onErrorChange={setRecipientsError} />
    </FlexGrid.Column>
  );

  const renderCommentsText = () => (
    <FlexGrid.Column>
      <Label>
        <span className={styles.labelText}>Comments</span>
        <TextArea autoHeight value={comments} onChange={e => setComments(e.target.value)} />
      </Label>
    </FlexGrid.Column>
  );

  const renderJobSettings = () => {
    switch (selectedJobType) {
      case JOBS.REVIEW_KEYWORD:
        return renderReviewKeywordSettings();
      case JOBS.RATING_CATEGORY_SCORE_CHANGE:
        return renderRatingScoreChangeSettings();
      case JOBS.STATION_PROFILE_PDF:
      case JOBS.STATION_REVIEWS_PDF:
      case JOBS.BRAND_ENGAGEMENT_PDF:
        return renderBasePDFParameters();
      case JOBS.STATION_RATINGS_PDF:
        return renderStationRatingsPDFParameters();
      case JOBS.COMPETITOR_BENCHMARK_PDF:
        return renderCompetitorBenchmarkPDFParameters();
      default:
        return null;
    }
  };

  return (
    <BPModal
      title="Schedule Report"
      show={showModal}
      loading={loading}
      disableSave={loading}
      saveButtonText="Save"
      deleteButtonText={job ? 'Delete' : null}
      deleteConfirmationMessage="Are you sure you want to delete this report?"
      onSave={handleOnSaveClick}
      onClose={handleOnClose}
      onDelete={job ? handleOnDelete : null}
      ctaButtons={[
        <Button secondary onClick={handleOnClose} wide>Cancel</Button>,
      ]}
    >
      <FlexGrid>
        <FlexGrid.Column>
          <Label htmlFor="jobTypeDropdown">
            <span className={styles.labelText}>
                  Report Type
              <span className={styles.requiredInput}> *</span>
            </span>
            <Dropdown
              id="jobTypeDropdown"
              animated
              bordered
              options={jobTypeOptions}
              onChange={e => setSelectedJobType(e.target.value)}
              placeholder="Select a report type"
              value={selectedJobType}
              disabled={job?.id}
            />
            {jobTypeError && <span className={styles.errorMessage}>{jobTypeError}</span>}
          </Label>
        </FlexGrid.Column>
        {selectedJobType ? (
          <>
            <FlexGrid.Column mobile={9}>
              <Label htmlFor="jobName">
                <span className={styles.labelText}>
                      Report Name
                  <span className={styles.requiredInput}> *</span>
                </span>
                <Input
                  bordered
                  value={jobName}
                  onChange={e => setJobName(e.target.value)}
                  placeholder="Name of the Report"
                  id="jobName"
                  error={jobNameError}
                />
              </Label>
            </FlexGrid.Column>
            <FlexGrid.Column mobile={3} className={styles.alertToggle}>
              <Label htmlFor="enableJob" className={styles.labelText}>
                    Enable Job
                <Checkbox
                  id="enableJob"
                  className={styles.checkbox}
                  onChange={() => setEnableJob(!enableJob)}
                  checked={enableJob}
                  toggle
                />
              </Label>
            </FlexGrid.Column>
            <FlexGrid.Column>
              { selectedJobType !== JOBS.STATION_PROFILE_PDF && !stationList
                ? (
                  <FlexGrid.Column className={styles.bottomMargin}>
                    <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
                    <b>&nbsp;Selected Station List has been deleted. Please select a new station list.</b>
                  </FlexGrid.Column>
                ) : null }
              { selectedJobType === JOBS.STATION_PROFILE_PDF ? (
                <Label htmlFor="stationsDropdown" className={styles.labelText}>
                      Selected Station
                  <StationSelector
                    watchStations={false}
                    onReady={() => setStationSelectorLoaded(true)}
                    onChange={setStation}
                    className={styles.stationSelector}
                    doNotStoreChanges
                    station={station}
                  />
                </Label>
              ) : (
                <StationListSelector onChange={handleOnStationListChange} stationLists={stationLists || []} Label="Selected Station List" />
              )}
            </FlexGrid.Column>
            { stationList
              ? (
                <FlexGrid.Column className={styles.bottomMargin}>
                  <b>{`${stationList.station_count} stations`}</b>
                </FlexGrid.Column>
              ) : null }
            {renderJobSettings()}
          </>
        ) : null}
      </FlexGrid>
    </BPModal>
  );
}

JobModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onAdd: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  ratingCategories: RatingCategoryListPropTypes.isRequired,
  stationLists: StationListsPropTypes.isRequired,
  stations: BasicStationsPropTypes.isRequired,
  job: JobPropTypes,
  brands: PropTypes.arrayOf(BrandPropTypes).isRequired,
  organizationSolutions: PropTypes.arrayOf(PropTypes.string),
  email: PropTypes.string,
};

JobModal.defaultProps = {
  onClose: () => {},
  onAdd: () => {},
  onDelete: () => {},
  onEdit: () => {},
  job: null,
  organizationSolutions: [],
  email: '',
};
